import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  useTheme,
  Box,
  Button,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import { CustomTextField, SectionTitle } from "~components/atoms";
import { useStreamContext } from "~contexts/Stream";
import { authFetch } from "~helpers/authFetch";
import { useToastNotificationContext } from "~contexts/ToastNotification";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`config-tabpanel-${index}`}
      aria-labelledby={`config-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// Estrutura de cores organizadas por categoria
// Baseado na lista fornecida: categoria + nome = chave completa
const COLOR_CATEGORIES = [
  {
    category: "surface",
    label: "surface",
    colors: [
      { key: "Background", label: "background" },
      { key: "Card", label: "card" },
      { key: "Container", label: "container" },
      { key: "Disabled", label: "disabled" },
      { key: "Inverse", label: "inverse" },
      { key: "Shadow", label: "shadow" },
    ],
  },
  {
    category: "surfaceBrand",
    label: "surface-brand",
    colors: [
      { key: "Background", label: "background" },
      { key: "Secondary", label: "secondary" },
      { key: "Stroke", label: "stroke" },
      { key: "Line", label: "line" },
      { key: "LineDisabled", label: "line disabled" },
      { key: "ComponenteCard", label: "componente card" },
      { key: "ComponenteTopbar", label: "componente topbar" },
      { key: "Highlight", label: "highlight" },
      { key: "Onbrand", label: "onbrand" },
    ],
  },
  {
    category: "surfaceBrandInverse",
    label: "surface-brand-inverse",
    colors: [
      { key: "Background", label: "background" },
      { key: "Secondary", label: "secondary" },
      { key: "Stroke", label: "stroke" },
      { key: "Line", label: "line" },
      { key: "LineDisabled", label: "line disabled" },
    ],
  },
  {
    category: "content",
    label: "content",
    colors: [
      { key: "Pure", label: "pure" },
      { key: "01", label: "01" },
      { key: "02", label: "02" },
      { key: "03", label: "03" },
      { key: "04", label: "04" },
      { key: "Inverse", label: "inverse" },
      { key: "BrandOnbrand", label: "brand onbrand" },
    ],
  },
  {
    category: "feedbackSuccess",
    label: "feedback-success",
    colors: [
      { key: "Content", label: "content" },
      { key: "Surface", label: "surface" },
      { key: "Container", label: "container" },
    ],
  },
  {
    category: "feedbackError",
    label: "feedback-error",
    colors: [
      { key: "Content", label: "content" },
      { key: "Surface", label: "surface" },
      { key: "Container", label: "container" },
      { key: "Stroke", label: "stroke" },
      { key: "Line", label: "line" },
    ],
  },
  {
    category: "feedbackInfo",
    label: "feedback-info",
    colors: [
      { key: "Content", label: "content" },
      { key: "Surface", label: "surface" },
      { key: "Container", label: "container" },
    ],
  },
  {
    category: "feedbackAlert",
    label: "feedback-alert",
    colors: [
      { key: "Content", label: "content" },
      { key: "Surface", label: "surface" },
      { key: "Container", label: "container" },
    ],
  },
  {
    category: "risk",
    label: "risk",
    colors: [
      { key: "Low", label: "low", fixed: true, value: "D6A757" },
      { key: "Medium", label: "medium", fixed: true, value: "B1560C" },
      { key: "High", label: "high", fixed: true, value: "A03127" },
    ],
  },
];

// Função auxiliar para gerar a chave completa (categoria + key)
const getColorKey = (category: string, key: string): string => {
  return `${category}${key}`;
};

// Estrutura de tipografia
// Família: primária e secundária (link Google Fonts)
// Peso: normal e bold (números)
// Tamanho: xxxxs, xxxs, xxs, xs, md (números)
const TYPOGRAPHY_FIELDS = {
  family: [
    { key: "fontFamilyPrimary", label: "primária", type: "url" },
    { key: "fontFamilySecondary", label: "secundária", type: "url" },
  ],
  weight: [
    { key: "fontWeightNormal", label: "normal", type: "weight", size: "normal" },
    { key: "fontWeightBold", label: "bold", type: "weight", size: "bold" },
  ],
  size: [
    { key: "fontSizeMd", label: "title (size-md)", type: "size", size: "md" },
    { key: "fontSizeXs", label: "paragraph (size-xs)", type: "size", size: "xs" },
    { key: "fontSizeXxs", label: "description (size-xxs)", type: "size", size: "xxs" },
    { key: "fontSizeXxxs", label: "caption (size-xxxs)", type: "size", size: "xxxs" },
    { key: "fontSizeXxxxs", label: "micro (size-xxxxs)", type: "size", size: "xxxxs" },
  ],
};

// Função auxiliar para gerar a chave completa de tipografia
const getTypographyKey = (key: string): string => {
  return key;
};

// Estrutura de border radius
const BORDER_RADIUS_FIELDS = [
  { key: "borderRadiusGeneral", label: "geral" },
  { key: "borderRadiusTags", label: "tags" },
];

// Componente de input de tipografia
interface TypographyInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  theme: any;
  type: "url" | "weight" | "size";
  placeholder?: string;
}

const TypographyInput: React.FC<TypographyInputProps> = ({ 
  label, 
  value, 
  onChange, 
  theme, 
  type,
  placeholder 
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;
    
    if (type === "weight" || type === "size") {
      // Apenas números para peso e tamanho
      inputValue = inputValue.replace(/[^0-9]/g, "");
    }
    
    onChange(inputValue);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
        width: "calc(50% - 8px)",
        minWidth: 0,
        flexShrink: 0,
        position: "relative",
      }}
    >
      <Box
        sx={{
          flex: 1,
          position: "relative",
          width: "100%",
        }}
      >
        <CustomTextField
          value={value}
          onChange={handleChange}
          label={label}
          fullWidth
        />
      </Box>
    </Box>
  );
};

// Componente de input de cor com preview
interface ColorInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  theme: any;
  disabled?: boolean;
}

const ColorInput: React.FC<ColorInputProps> = ({ label, value, onChange, theme, disabled = false }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value.toUpperCase();
    // Remover caracteres inválidos (manter apenas 0-9, A-F)
    inputValue = inputValue.replace(/[^0-9A-F]/g, "");
    // Limitar a 8 caracteres (sem #) - aceita hex com opacidade
    if (inputValue.length > 8) {
      inputValue = inputValue.substring(0, 8);
    }
    onChange(inputValue);
  };

  // Função para converter valor parcial em cor válida
  const getDisplayColor = (val: string): string => {
    if (!val || val.length === 0) {
      return "transparent";
    }
    
    // Se tem 8 caracteres (hex com opacidade), adicionar # e mostrar
    if (val.length === 8) {
      return `#${val}`;
    }
    
    // Se tem 6 caracteres (hex sem opacidade), adicionar # e mostrar
    if (val.length === 6) {
      return `#${val}`;
    }
    
    // Se tem menos de 6, completar com zeros à direita até 6 caracteres
    if (val.length > 0 && val.length < 6) {
      const padded = val.padEnd(6, "0");
      return `#${padded}`;
    }
    
    // Se tem 7 caracteres, completar com zero para ter 8 (opacidade)
    if (val.length === 7) {
      return `#${val}0`;
    }
    
    return "transparent";
  };

  const displayColor = getDisplayColor(value);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
        width: "calc(50% - 8px)",
        minWidth: 0,
        flexShrink: 0,
        position: "relative",
      }}
    >
      <Box
        sx={{
          width: "56px",
          height: "56px",
          borderRadius: "8px",
          backgroundColor: displayColor,
          border: `1px solid ${theme.palette.grey[700]}`,
          flexShrink: 0,
        }}
      />
      <Box
        sx={{
          flex: 1,
          position: "relative",
          width: "100%",
        }}
      >
        <CustomTextField
          label={label}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          fullWidth
          placeholder={disabled ? "" : "000000"}
          inputProps={{
            style: {
              fontFamily: "monospace",
            },
          }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              "&.Mui-disabled": {
                backgroundColor: theme.palette.grey[900],
                opacity: 1,
              },
            },
            "& .MuiInputBase-input": {
              "&.Mui-disabled": {
                WebkitTextFillColor: theme.palette.grey[0],
                opacity: 1,
              },
            },
            "& .MuiInputBase-input::placeholder": {
              color: theme.palette.grey[600],
              opacity: 1,
            },
          }}
        />
      </Box>
    </Box>
  );
};

const ConfigsGerais: React.FC = () => {
  const theme: any = useTheme();
  const { currentStream } = useStreamContext();
  const { setToastData } = useToastNotificationContext();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    sales_channel: "",
    stream_name: "",
    frontend_url: "",
    ecommerce_url: "",
    checkout_url: "",
  });
  // Inicializar cores fixas (risk) no estado
  const [colorsData, setColorsData] = useState<Record<string, string>>(() => {
    const initialColors: Record<string, string> = {};
    COLOR_CATEGORIES.forEach((categoryGroup) => {
      categoryGroup.colors.forEach((color) => {
        if ((color as any).fixed === true) {
          const colorKey = getColorKey(categoryGroup.category, color.key);
          initialColors[colorKey] = (color as any).value || "";
        }
      });
    });
    return initialColors;
  });

  // Estado para tipografia
  const [typographyData, setTypographyData] = useState<Record<string, string>>({});

  // Estado para border radius
  const [borderRadiusData, setBorderRadiusData] = useState<Record<string, string>>({});

  const loadStreamData = useCallback(async () => {
    if (!currentStream?.id) return;
    
    try {
      setLoading(true);
      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/stream/as-it-is/${currentStream.id}`,
        method: "GET",
      });

      if (response.ok) {
        const data = await response.json();
        setFormData({
          sales_channel: String(data.sales_channel || ""),
          stream_name: data.stream_name || "",
          frontend_url: data.brandDetails?.frontend_url || "",
          ecommerce_url: data.brandDetails?.ecommerce_url || "",
          checkout_url: data.brandDetails?.checkout_url || "",
        });
      }
    } catch (error) {
      console.error("Error loading stream data:", error);
      setToastData(
        {
          title: "Erro",
          text: "Erro ao carregar dados da stream",
        },
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [currentStream, setToastData]);

  const loadColorsData = useCallback(async () => {
    if (!currentStream?.id) return;

    try {
      setLoading(true);
      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/brand-templates/${currentStream.id}/cores`,
        method: "GET",
      });

      if (response.ok) {
        const data = await response.json();
        // Converter array de templates para objeto { colorKey: value }
        const colors: Record<string, string> = {};
        
        // Primeiro, inicializar com cores fixas (risk)
        COLOR_CATEGORIES.forEach((categoryGroup) => {
          categoryGroup.colors.forEach((color) => {
            if ((color as any).fixed === true) {
              const colorKey = getColorKey(categoryGroup.category, color.key);
              colors[colorKey] = (color as any).value || "";
            }
          });
        });
        
        // Depois, adicionar cores do backend (não sobrescrever cores fixas)
        if (Array.isArray(data)) {
          data.forEach((item: any) => {
            if (item.config_key && item.config_value) {
              // Não sobrescrever cores fixas
              if (!item.config_key.startsWith("risk")) {
                // Remover # se existir para exibição no input
                colors[item.config_key] = item.config_value.replace("#", "");
              }
            }
          });
        }
        setColorsData(colors);
      } else if (response.status === 404) {
        // Não tem cores salvas ainda, manter apenas cores fixas
        const fixedColors: Record<string, string> = {};
        COLOR_CATEGORIES.forEach((categoryGroup) => {
          categoryGroup.colors.forEach((color) => {
            if ((color as any).fixed === true) {
              const colorKey = getColorKey(categoryGroup.category, color.key);
              fixedColors[colorKey] = (color as any).value || "";
            }
          });
        });
        setColorsData(fixedColors);
      }
    } catch (error) {
      console.error("Error loading colors data:", error);
      // Não mostrar erro se for 404 (ainda não tem cores salvas)
      setColorsData({});
    } finally {
      setLoading(false);
    }
  }, [currentStream]);

  const loadTypographyData = useCallback(async () => {
    if (!currentStream?.id) return;

    try {
      setLoading(true);
      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/brand-templates/${currentStream.id}/tipografia`,
        method: "GET",
      });

      const initialTypography: Record<string, string> = {};

      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          data.forEach((item: any) => {
            if (item.config_key && item.config_value) {
              initialTypography[item.config_key] = item.config_value;
            }
          });
        }
        setTypographyData(initialTypography);
      } else if (response.status === 404) {
        // Não tem tipografia salva ainda
        setTypographyData(initialTypography);
      }
    } catch (error) {
      console.error("Error loading typography data:", error);
      setTypographyData({});
    } finally {
      setLoading(false);
    }
  }, [currentStream]);

  const handleTypographyChange = useCallback((key: string, value: string) => {
    const typographyKey = getTypographyKey(key);
    setTypographyData((prev) => ({
      ...prev,
      [typographyKey]: value,
    }));
  }, []);

  const handleSaveTypography = useCallback(async () => {
    if (!currentStream?.id) {
      setToastData(
        {
          title: "Erro",
          text: "Nenhuma stream selecionada",
        },
        "error"
      );
      return;
    }

    try {
      setSaving(true);
      // Preparar dados para salvar (apenas campos preenchidos)
      const typographyToSave = Object.entries(typographyData)
        .filter(([_, value]) => value && value.trim() !== "")
        .map(([config_key, config_value]) => ({
          config_key,
          config_value: config_value.trim(),
        }));

      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/brand-templates/${currentStream.id}/tipografia`,
        method: "PATCH",
        data: {
          typography: typographyToSave,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || "Erro ao salvar tipografia");
        (error as any).status = response.status;
        throw error;
      }

      setToastData(
        {
          title: "Sucesso",
          text: "Tipografia salva com sucesso",
        },
        "success"
      );
    } catch (error: any) {
      console.error("Error saving typography:", error);
      let errorMessage = "Erro ao salvar tipografia";

      if (error.status === 400) {
        errorMessage = error.message || "Dados inválidos";
      }

      setToastData(
        {
          title: "Erro",
          text: errorMessage,
        },
        "error"
      );
    } finally {
      setSaving(false);
    }
  }, [currentStream, typographyData, setToastData]);

  const loadBorderRadiusData = useCallback(async () => {
    if (!currentStream?.id) return;

    try {
      setLoading(true);
      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/brand-templates/${currentStream.id}/border-radius`,
        method: "GET",
      });

      const initialBorderRadius: Record<string, string> = {};

      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          data.forEach((item: any) => {
            if (item.config_key && item.config_value) {
              initialBorderRadius[item.config_key] = item.config_value;
            }
          });
        }
        setBorderRadiusData(initialBorderRadius);
      } else if (response.status === 404) {
        // Não tem border radius salvo ainda
        setBorderRadiusData(initialBorderRadius);
      }
    } catch (error) {
      console.error("Error loading border radius data:", error);
      setBorderRadiusData({});
    } finally {
      setLoading(false);
    }
  }, [currentStream]);

  const handleBorderRadiusChange = useCallback((key: string, value: string) => {
    setBorderRadiusData((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const handleSaveBorderRadius = useCallback(async () => {
    if (!currentStream?.id) {
      setToastData(
        {
          title: "Erro",
          text: "Nenhuma stream selecionada",
        },
        "error"
      );
      return;
    }

    try {
      setSaving(true);
      // Preparar dados para salvar (apenas campos preenchidos)
      const borderRadiusToSave = Object.entries(borderRadiusData)
        .filter(([_, value]) => value && value.trim() !== "")
        .map(([config_key, config_value]) => ({
          config_key,
          config_value: config_value.trim(),
        }));

      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/brand-templates/${currentStream.id}/border-radius`,
        method: "PATCH",
        data: {
          borderRadius: borderRadiusToSave,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || "Erro ao salvar border radius");
        (error as any).status = response.status;
        throw error;
      }

      setToastData(
        {
          title: "Sucesso",
          text: "Border radius salvo com sucesso",
        },
        "success"
      );
    } catch (error: any) {
      console.error("Error saving border radius:", error);
      let errorMessage = "Erro ao salvar border radius";

      if (error.status === 400) {
        errorMessage = error.message || "Dados inválidos";
      }

      setToastData(
        {
          title: "Erro",
          text: errorMessage,
        },
        "error"
      );
    } finally {
      setSaving(false);
    }
  }, [currentStream, borderRadiusData, setToastData]);

  useEffect(() => {
    if (currentStream?.id) {
      loadStreamData();
      if (tabValue === 2) {
        loadColorsData();
      }
      if (tabValue === 1) {
        loadTypographyData();
      }
      if (tabValue === 3) {
        loadBorderRadiusData();
      }
    }
  }, [currentStream, tabValue, loadStreamData, loadColorsData, loadTypographyData, loadBorderRadiusData]);

  useEffect(() => {
    if (currentStream?.id && tabValue === 2) {
      loadColorsData();
    }
    if (currentStream?.id && tabValue === 1) {
      loadTypographyData();
    }
    if (currentStream?.id && tabValue === 3) {
      loadBorderRadiusData();
    }
  }, [tabValue, currentStream, loadColorsData, loadTypographyData, loadBorderRadiusData]);

  const handlePublish = async () => {
    if (!currentStream?.id) {
      setToastData(
        {
          title: "Erro",
          text: "Nenhuma stream selecionada",
        },
        "error"
      );
      return;
    }

    try {
      setSaving(true);
      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/stream/${currentStream.id}/general-configs`,
        method: "PATCH",
        data: {
          sales_channel: parseInt(formData.sales_channel) || 1,
          stream_name: formData.stream_name.trim(),
          frontend_url: formData.frontend_url.trim().replace(/\/$/, ''),
          ecommerce_url: formData.ecommerce_url.trim().replace(/\/$/, ''),
          checkout_url: formData.checkout_url.trim().replace(/\/$/, ''),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || "Erro ao salvar configurações");
        (error as any).status = response.status;
        throw error;
      }

      setToastData(
        {
          title: "Sucesso",
          text: "Configurações salvas com sucesso",
        },
        "success"
      );
    } catch (error: any) {
      console.error("Error saving configs:", error);
      let errorMessage = "Erro ao salvar configurações";
      
      if (error.status === 400) {
        errorMessage = error.message || "Dados inválidos ou stream name já existe";
      }
      
      setToastData(
        {
          title: "Erro",
          text: errorMessage,
        },
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleColorChange = (category: string, key: string, value: string) => {
    // Não permitir alteração de cores fixas (risk)
    if (category === "risk") {
      return;
    }
    const colorKey = getColorKey(category, key);
    // Remover # se existir e salvar até 8 caracteres hex (permite opacidade)
    const cleanValue = value.replace("#", "").toUpperCase().substring(0, 8);
    setColorsData((prev) => ({
      ...prev,
      [colorKey]: cleanValue,
    }));
  };

  const handleSaveColors = async () => {
    if (!currentStream?.id) {
      setToastData(
        {
          title: "Erro",
          text: "Nenhuma stream selecionada",
        },
        "error"
      );
      return;
    }

    try {
      setSaving(true);
      // Preparar dados para salvar (apenas cores preenchidas com 6 ou 8 caracteres)
      // Excluir cores fixas (risk) da lista de salvamento
      const colorsToSave = Object.entries(colorsData)
        .filter(([config_key, value]) => {
          // Não salvar cores fixas (risk)
          if (config_key.startsWith("risk")) {
            return false;
          }
          return value && value.trim() !== "" && (value.length === 6 || value.length === 8);
        })
        .map(([config_key, config_value]) => ({
          config_key,
          config_value: `#${config_value}`,
        }));

      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/brand-templates/${currentStream.id}/cores`,
        method: "PATCH",
        data: {
          colors: colorsToSave,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || "Erro ao salvar cores");
        (error as any).status = response.status;
        throw error;
      }

      setToastData(
        {
          title: "Sucesso",
          text: "Cores salvas com sucesso",
        },
        "success"
      );
    } catch (error: any) {
      console.error("Error saving colors:", error);
      let errorMessage = "Erro ao salvar cores";

      if (error.status === 400) {
        errorMessage = error.message || "Dados inválidos";
      }
      
      setToastData(
        {
          title: "Erro",
          text: errorMessage,
        },
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!currentStream) {
    return (
      <Box>
        <Typography variant="h4" sx={{ color: theme.palette.grey[0], mb: 0.5, fontSize: "30px !important" }}>
          configurações gerais
        </Typography>
        <Typography variant="body2" sx={{ color: "#858585", fontSize: "15px" }}>
          Selecione uma marca para editar as configurações
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ color: theme.palette.grey[0], mb: 0.5, fontSize: "30px !important" }}>
            configurações gerais
          </Typography>
          <Typography variant="body2" sx={{ color: "#858585", mb: "40px", fontSize: "15px" }}>
            informações de identidade e tom da marca
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={
            tabValue === 1 
              ? handleSaveTypography 
              : tabValue === 2 
              ? handleSaveColors 
              : tabValue === 3
              ? handleSaveBorderRadius
              : handlePublish
          }
          disabled={saving || loading}
          sx={{
            backgroundColor: "#FFFFFF",
            color: "#000000",
            borderRadius: "100px",
            paddingTop: "16px",
            paddingRight: "24px",
            paddingBottom: "16px",
            paddingLeft: "24px",
            height: "48px",
            gap: "12px",
            fontWeight: 400,
            fontSize: "11px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#F5F5F5",
            },
            "&:disabled": {
              backgroundColor: "#666666",
              color: "#FFFFFF",
            },
          }}
        >
          {saving ? (
            <>
              <CircularProgress size={16} sx={{ color: "#000000" }} />
              salvando...
            </>
          ) : (
            <>
              publicar
              <Box
                component="img"
                src="/icons/publish.svg"
                alt="publicar"
                sx={{
                  width: "24px",
                  height: "24px",
                }}
              />
            </>
          )}
        </Button>
      </Box>

      <Box sx={{ maxWidth: "720px" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: "#FFFFFF",
              height: "2px",
            },
            "& .MuiTab-root": {
              color: theme.palette.grey[400],
              textTransform: "none",
              fontSize: "15px",
              fontWeight: 400,
              padding: "16px 24px",
              minHeight: "56px",
              "&.Mui-selected": {
                color: "#FFFFFF",
              },
            },
          }}
        >
          <Tab label="políticas" />
          <Tab label="tipografia" />
          <Tab label="cores" />
          <Tab label="border radius" />
        </Tabs>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box>
                <SectionTitle sx={{ mb: 1.5 }}>
                  política comercial (sales channel)
                </SectionTitle>
                <CustomTextField
                  label="número da política"
                  value={formData.sales_channel}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sales_channel: e.target.value,
                    }))
                  }
                  fullWidth
                  borderRadius="8px"
                />
              </Box>

              <Box>
                <SectionTitle sx={{ mb: 1.5 }}>
                  string da URL
                </SectionTitle>
                <CustomTextField
                  label="string"
                  value={formData.stream_name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      stream_name: e.target.value,
                    }))
                  }
                  fullWidth
                  borderRadius="8px"
                />
              </Box>

              <Box>
                <SectionTitle sx={{ mb: 1.5 }}>
                  frontend URL
                </SectionTitle>
                <CustomTextField
                  label="frontend URL"
                  value={formData.frontend_url}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      frontend_url: e.target.value,
                    }))
                  }
                  fullWidth
                  borderRadius="8px"
                />
              </Box>

              <Box>
                <SectionTitle sx={{ mb: 1.5 }}>
                  ecommerce URL
                </SectionTitle>
                <CustomTextField
                  label="ecommerce URL"
                  value={formData.ecommerce_url}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ecommerce_url: e.target.value,
                    }))
                  }
                  fullWidth
                  borderRadius="8px"
                />
              </Box>

              <Box>
                <SectionTitle sx={{ mb: 1.5 }}>
                  checkout URL
                </SectionTitle>
                <CustomTextField
                  label="checkout URL"
                  value={formData.checkout_url}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      checkout_url: e.target.value,
                    }))
                  }
                  fullWidth
                  borderRadius="8px"
                />
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box>
              {/* Família */}
              <Box sx={{ mb: 4 }}>
                <SectionTitle sx={{ mb: 0 }}>
                  família
                </SectionTitle>
                <Typography
                  sx={{
                    color: "#858585",
                    fontSize: "11px",
                    fontWeight: 400,
                    mb: 2,
                    mt: 0,
                  }}
                >
                  link da fonte
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  {TYPOGRAPHY_FIELDS.family.map((field) => {
                    const typographyKey = getTypographyKey(field.key);
                    const currentValue = typographyData[typographyKey] || "";
                    return (
                      <TypographyInput
                        key={field.key}
                        label={field.label}
                        value={currentValue}
                        onChange={(value) =>
                          handleTypographyChange(field.key, value)
                        }
                        theme={theme}
                        type={field.type as "url" | "weight" | "size"}
                        placeholder="https://fonts.googleapis.com/css2?family=..."
                      />
                    );
                  })}
                </Box>
              </Box>
              
              {/* Peso */}
              <Box sx={{ mb: 4 }}>
                <SectionTitle sx={{ mb: 0 }}>
                  peso
                </SectionTitle>
                <Typography
                  sx={{
                    color: "#858585",
                    fontSize: "11px",
                    fontWeight: 400,
                    mb: 2,
                  }}
                >
                  escrever em números (ex: 300, 400, 500...)
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  {TYPOGRAPHY_FIELDS.weight.map((field) => {
                    const typographyKey = getTypographyKey(field.key);
                    const currentValue = typographyData[typographyKey] || "";
                    return (
                      <TypographyInput
                        key={field.key}
                        label={field.label}
                        value={currentValue}
                        onChange={(value) =>
                          handleTypographyChange(field.key, value)
                        }
                        theme={theme}
                        type={field.type as "url" | "weight" | "size"}
                        placeholder="300, 400, 500..."
                      />
                    );
                  })}
                </Box>
              </Box>
              
              {/* Tamanho */}
              <Box sx={{ mb: 4 }}>
                <SectionTitle sx={{ mb: 0 }}>
                  tamanho
                </SectionTitle>
                <Typography
                  sx={{
                    color: "#858585",
                    fontSize: "11px",
                    fontWeight: 400,
                    mb: 2,
                  }}
                >
                  escrever em números (ex: 18, 16, 11...)
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  {TYPOGRAPHY_FIELDS.size.map((field) => {
                    const typographyKey = getTypographyKey(field.key);
                    const currentValue = typographyData[typographyKey] || "";
                    return (
                      <TypographyInput
                        key={field.key}
                        label={field.label}
                        value={currentValue}
                        onChange={(value) =>
                          handleTypographyChange(field.key, value)
                        }
                        theme={theme}
                        type={field.type as "url" | "weight" | "size"}
                        placeholder="18, 16, 11..."
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box>
              <Typography
                sx={{
                  color: theme.palette.grey[400],
                  fontSize: "15px",
                  mb: 3,
                }}
              >
                cada cor em código HEX (000000, FFFFFF, 3F4DF3)
              </Typography>
              {COLOR_CATEGORIES.map((categoryGroup, groupIndex) => (
                <Box key={groupIndex} sx={{ mb: 4 }}>
                  <Typography
                    sx={{
                      color: theme.palette.grey[0],
                      fontSize: "15px",
                      fontWeight: 400,
                      mb: 2,
                      textTransform: "lowercase",
                    }}
                  >
                    {categoryGroup.label}
            </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    {categoryGroup.colors.map((color) => {
                      const colorKey = getColorKey(categoryGroup.category, color.key);
                      // Se for uma cor fixa, usar o valor fixo, senão usar o valor do estado
                      const isFixed = (color as any).fixed === true;
                      const fixedValue = (color as any).value || "";
                      const currentValue = isFixed ? fixedValue : (colorsData[colorKey] || "");
                      return (
                        <ColorInput
                          key={color.key}
                          label={color.label}
                          value={currentValue.replace("#", "")}
                          onChange={(value) =>
                            handleColorChange(categoryGroup.category, color.key, value)
                          }
                          theme={theme}
                          disabled={isFixed}
                        />
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Box>
              <Typography
                sx={{
                  color: "#858585",
                  fontSize: "11px",
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                valor em pixels. apenas numeros (ex: 8, 12, 24)
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  width: "100%",
                }}
              >
                {BORDER_RADIUS_FIELDS.map((field) => {
                  const currentValue = borderRadiusData[field.key] || "";
                  return (
                    <TypographyInput
                      key={field.key}
                      label={field.label}
                      value={currentValue}
                      onChange={(value) =>
                        handleBorderRadiusChange(field.key, value)
                      }
                      theme={theme}
                      type="size"
                      placeholder="0, 4, 8, 16, 32..."
                    />
                  );
                })}
              </Box>
            </Box>
          </TabPanel>
        </>
      )}
      </Box>
    </Box>
  );
};

export default ConfigsGerais;
